/**
 * AnimationManager — Core Layer state container for GLB animation playback.
 *
 * Pure state + Three.js AnimationMixer logic.  No React, no R3F.
 * The R3F `AnimationManagerTicker` component calls `tick(delta)` each frame.
 *
 * Responsibilities:
 * - Initialise AnimationMixer from the cloned scene + GLTF AnimationClips
 * - Play / stop / reverse individual clips or all at once
 * - Track per-clip state (playing, direction, position)
 * - Support mid-animation direction reversal without jumps
 * - Jump-to-end for instant state changes (no animation)
 * - Emit callbacks on start / finish
 * - Clean up mixer + actions on dispose
 *
 * @see docs/ims-engine-api/09-feature-animation-lighting.md
 * @see docs/ims-engine-api/02-api-contract.md
 */

import * as THREE from 'three';
import type {
  AnimationDirection,
  AnimationPlayOptions,
  AnimationClipState,
  AnimationState,
  AnimationStartedEvent,
  AnimationFinishedEvent,
} from '../api/types/animation';

// ---------------------------------------------------------------------------
// Internal per-clip tracking
// ---------------------------------------------------------------------------

interface ClipEntry {
  clip: THREE.AnimationClip;
  action: THREE.AnimationAction;
  direction: AnimationDirection;
  playing: boolean;
  /** Logical position: 'start' | 'end' | 'mid'. */
  position: 'start' | 'end' | 'mid';
}

// ---------------------------------------------------------------------------
// AnimationManager
// ---------------------------------------------------------------------------

export class AnimationManager {
  private _mixer: THREE.AnimationMixer | null = null;
  private _clips: Map<string, ClipEntry> = new Map();
  private _root: THREE.Object3D | null = null;

  /** Called when a clip starts playing. */
  onStarted?: (event: AnimationStartedEvent) => void;
  /** Called when a clip finishes playing. */
  onFinished?: (event: AnimationFinishedEvent) => void;
  /**
   * Called when visual state changes and at least one render frame is needed.
   * The R3F ticker wires this to `invalidate()` for `frameloop="demand"`.
   */
  onNeedsRender?: () => void;

  // -----------------------------------------------------------------------
  // init
  // -----------------------------------------------------------------------

  /**
   * Initialise the mixer from the cloned scene and the GLTF animation clips.
   *
   * Must be called once from `onMounted(clonedScene)` in createEngine.
   * Clips are the original `gltf.animations` — they reference bone/object
   * names that match the cloned scene hierarchy.
   *
   * @param root    The cloned scene (the Object3D tree that is rendered)
   * @param clips   AnimationClip[] from the loaded GLTF
   */
  init(root: THREE.Object3D, clips: THREE.AnimationClip[]): void {
    this.dispose();

    this._root = root;
    this._mixer = new THREE.AnimationMixer(root);

    // Listen for 'finished' events from the mixer
    this._mixer.addEventListener('finished', this._onMixerFinished);

    for (const clip of clips) {
      const action = this._mixer.clipAction(clip);
      // Configure for toggle-style playback:
      // - Play once, don't loop
      // - Hold at final frame
      action.setLoop(THREE.LoopOnce, 1);
      action.clampWhenFinished = true;

      this._clips.set(clip.name, {
        clip,
        action,
        direction: 'forward',
        playing: false,
        position: 'start',
      });
    }
  }

  // -----------------------------------------------------------------------
  // play
  // -----------------------------------------------------------------------

  /**
   * Play a single clip by name.
   *
   * - If already playing in the **same direction** → no-op (idempotent).
   * - If already playing in the **opposite direction** → reverses from
   *   current time (no jump).
   * - If at the **target end** (e.g. forward & already at 'end') → no-op.
   * - If stopped → starts from the appropriate end.
   */
  play(name: string, options?: AnimationPlayOptions): void {
    const entry = this._clips.get(name);
    if (!entry) {
      console.warn(`[AnimationManager] Unknown clip: "${name}"`);
      return;
    }

    const direction = options?.direction ?? 'forward';
    const speed = Math.abs(options?.speed ?? 1);

    // Already at target end → no-op
    if (!entry.playing) {
      if (direction === 'forward' && entry.position === 'end') return;
      if (direction === 'reverse' && entry.position === 'start') return;
    }

    // Already playing in the same direction → just update speed
    if (entry.playing && entry.direction === direction) {
      entry.action.timeScale = direction === 'forward' ? speed : -speed;
      return;
    }

    const action = entry.action;
    const timeScale = direction === 'forward' ? speed : -speed;

    if (entry.playing) {
      // Mid-animation reversal: just flip timeScale, continue from current time
      action.timeScale = timeScale;
    } else {
      // Starting fresh: set time to appropriate start point
      action.paused = false;
      action.enabled = true;
      action.timeScale = timeScale;

      if (direction === 'forward') {
        // If at start, reset to 0; if at mid/end, the user explicitly wants forward
        if (entry.position === 'start') {
          action.time = 0;
        }
        // If at 'end', reset to play forward again
        if (entry.position === 'end') {
          action.time = 0;
        }
      } else {
        // Reverse: if at end, start from duration; if at start → already handled (no-op above)
        if (entry.position === 'end') {
          action.time = entry.clip.duration;
        }
        if (entry.position === 'mid') {
          // Continue from current time
        }
      }

      action.play();
    }

    entry.direction = direction;
    entry.playing = true;
    entry.position = 'mid';

    this.onStarted?.({ clip: name, direction });
    this.onNeedsRender?.();
  }

  /**
   * Play all registered clips in the given direction.
   */
  playAll(options?: AnimationPlayOptions): void {
    for (const name of this._clips.keys()) {
      this.play(name, options);
    }
  }

  // -----------------------------------------------------------------------
  // stop
  // -----------------------------------------------------------------------

  /**
   * Stop a clip. The clip freezes at its current time (does not reset).
   */
  stop(name: string): void {
    const entry = this._clips.get(name);
    if (!entry || !entry.playing) return;

    entry.action.paused = true;
    entry.playing = false;
    this._updatePosition(entry);
    this.onNeedsRender?.();
  }

  /** Stop all clips. */
  stopAll(): void {
    for (const name of this._clips.keys()) {
      this.stop(name);
    }
  }

  // -----------------------------------------------------------------------
  // jump — instant state change (no animation)
  // -----------------------------------------------------------------------

  /**
   * Jump a clip to its end state instantly (no animation).
   *
   * @param name      Clip name
   * @param direction `'forward'` → jump to "open" (time = duration),
   *                  `'reverse'` → jump to "closed" (time = 0)
   */
  jump(name: string, direction: AnimationDirection): void {
    const entry = this._clips.get(name);
    if (!entry) {
      console.warn(`[AnimationManager] Unknown clip: "${name}"`);
      return;
    }

    const action = entry.action;

    // Stop any ongoing playback
    action.paused = true;
    action.enabled = true;

    if (direction === 'forward') {
      action.time = entry.clip.duration;
      entry.position = 'end';
    } else {
      action.time = 0;
      entry.position = 'start';
    }

    entry.playing = false;
    entry.direction = direction;

    // Force a single mixer update so the pose is applied immediately
    this._mixer?.update(0);
    this.onNeedsRender?.();
  }

  /** Jump all clips. */
  jumpAll(direction: AnimationDirection): void {
    for (const name of this._clips.keys()) {
      this.jump(name, direction);
    }
    // Single mixer update for all jumps
    this._mixer?.update(0);
    this.onNeedsRender?.();
  }

  // -----------------------------------------------------------------------
  // tick — called every frame by the R3F ticker
  // -----------------------------------------------------------------------

  /**
   * Maximum delta (seconds) applied per tick. Prevents animation skip in
   * `frameloop="demand"` mode where the first delta after idle equals
   * the entire idle period (e.g. 5 s), completing the animation instantly.
   */
  private static readonly MAX_TICK_DELTA = 1 / 30; // ~33 ms

  /**
   * Advance the mixer by `deltaSeconds`.
   * Called from the AnimationManagerTicker R3F component each frame.
   */
  tick(deltaSeconds: number): void {
    this._mixer?.update(Math.min(deltaSeconds, AnimationManager.MAX_TICK_DELTA));
  }

  // -----------------------------------------------------------------------
  // State queries
  // -----------------------------------------------------------------------

  /** Get the names of all registered animation clips. */
  getClipNames(): string[] {
    return Array.from(this._clips.keys());
  }

  /** Get the full animation state snapshot. */
  getState(): AnimationState {
    const clips: AnimationClipState[] = [];
    const playing: string[] = [];

    for (const [name, entry] of this._clips) {
      clips.push({
        name,
        playing: entry.playing,
        direction: entry.direction,
        position: entry.position,
        duration: entry.clip.duration,
      });
      if (entry.playing) playing.push(name);
    }

    return { clips, playing };
  }

  /** True when at least one clip is playing. */
  get isPlaying(): boolean {
    for (const entry of this._clips.values()) {
      if (entry.playing) return true;
    }
    return false;
  }

  // -----------------------------------------------------------------------
  // dispose
  // -----------------------------------------------------------------------

  /** Clean up mixer, actions, and all internal state. */
  dispose(): void {
    if (this._mixer) {
      this._mixer.removeEventListener('finished', this._onMixerFinished);
      this._mixer.stopAllAction();
      this._mixer.uncacheRoot(this._root!);
      // Uncache all clips
      for (const entry of this._clips.values()) {
        this._mixer!.uncacheClip(entry.clip);
        this._mixer!.uncacheAction(entry.clip);
      }
    }
    this._mixer = null;
    this._root = null;
    this._clips.clear();
    this.onStarted = undefined;
    this.onFinished = undefined;
    // NOTE: onNeedsRender is NOT cleared here — it is owned by the
    // AnimationManagerTicker R3F component and cleaned up via its useEffect
    // return. Clearing it here would break the invalidation bridge because
    // init() calls dispose() when re-initialising with a new model.
  }

  // -----------------------------------------------------------------------
  // Private
  // -----------------------------------------------------------------------

  /**
   * Handler for the AnimationMixer's 'finished' event.
   * Bound as arrow function so `this` is correct.
   */
  private _onMixerFinished = (event: { action: THREE.AnimationAction }) => {
    // Guard: mixer already disposed (race during teardown)
    if (!this._mixer) return;

    // Find the clip entry for this action
    for (const [name, entry] of this._clips) {
      if (entry.action === event.action) {
        entry.playing = false;
        // Determine final position based on direction
        if (entry.direction === 'forward') {
          entry.position = 'end';
        } else {
          entry.position = 'start';
        }
        this.onFinished?.({ clip: name, direction: entry.direction });
        break;
      }
    }
  };

  /** Update position of a stopped clip based on its current time. */
  private _updatePosition(entry: ClipEntry): void {
    const t = entry.action.time;
    const d = entry.clip.duration;
    const eps = 0.001;

    if (t <= eps) {
      entry.position = 'start';
    } else if (t >= d - eps) {
      entry.position = 'end';
    } else {
      entry.position = 'mid';
    }
  }
}
